import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import globalMessages from 'translations/messages';
import NoMatch from 'containers/NoMatch';
import Page from 'components/Page';
import Loading from 'components/Loading';

import { Tabs } from 'antd';

import CustomerSettings from './CustomerSettings';
import CustomerDataSources from './CustomerDataSources';
import CustomerUsers from './CustomerUsers';
import CustomerLicenses from './CustomerLicenses';
import CustomerPrivacyPolicy from './CustomerPrivacy';
import CustomerBilling from './CustomerBilling';
import CustomerOrders from './CustomerOrders';
import CustomerApiKeys from './CustomerApiKeys';
import messages from './messages';

const EditCustomer = ({ customerId }) => {
  const history = useHistory();
  const { path, url, params } = useRouteMatch();
  const { params: { tab = 'settings' } } = useRouteMatch(`${path}/:tab?`);

  const { data = {}, loading } = useQuery(gql`
    query EditCustomer($id: ID!) {
      customer (id: $id) {
        id
        name
        alias
        logo { url }
        pipedriveOrganizationId
        personalDataController
        allowPersonalData
        disableAI
        privacyPolicies {
          id
          type
          language
          url
          content
        }
        surveyLanguages { id name }
        dataSources {
          id
          title
          access {
            id
            customerAccess {
              id
              user { id }
            }
            roles {
              id
              permissions
            }
          }
          activeLicense {
            id
            license {
              id
              title
            }
            settings(keys: ["userLimit", "advancedUserLimit"])
          }
        }        
      }
    }
  `, {
    variables: {
      id: customerId || params.customerId,
    },
  });

  if (loading) {
    return <Loading />;
  }

  const { customer } = data;

  if (!customer) {
    return <NoMatch />;
  }

  const tabs = [{
    key: 'settings',
    label: <FormattedMessage {...globalMessages.settings} />,
    children: <CustomerSettings customer={customer} />,
  }, {
    key: 'licenses',
    label: <FormattedMessage {...globalMessages.licenses} />,
    children: <CustomerLicenses customer={customer} />,
  }, {
    key: 'dataSources',
    label: <FormattedMessage {...globalMessages.dataSources} />,
    children: <CustomerDataSources customer={customer} />,
  }, {
    key: 'users',
    label: <FormattedMessage {...globalMessages.users} />,
    children: <CustomerUsers customer={customer} />,
  }, {
    key: 'billing',
    label: <FormattedMessage {...messages.billing} />,
    children: <CustomerBilling customerId={customerId} />,
  }, {
    key: 'orders',
    label: <FormattedMessage {...messages.orders} />,
    children: <CustomerOrders customer={customer} />,
  }, {
    key: 'privacy',
    label: <FormattedMessage {...messages.privacy} />,
    children: <CustomerPrivacyPolicy customer={customer} />,
  }, {
    key: 'api-keys',
    label: <FormattedMessage {...messages.apiKeys} />,
    children: <CustomerApiKeys customer={customer} />,
  }];

  return (
    <Page>
      <Page.Header title={customer?.name} />
      <Page.Content>
        <Tabs onChange={(activeKey) => history.push(`${url}/${activeKey}`)} activeKey={tab} items={tabs} destroyInactiveTabPane />
      </Page.Content>
    </Page>
  );
};

export default EditCustomer;
